import React, { useEffect, useRef, useState, FC, useId, ChangeEvent } from 'react'
import { Conversation } from '@twilio/conversations'
import { Capacitor } from '@capacitor/core'
import { useTranslation } from 'react-i18next'

import { EmojiPicker } from './components'

import styles from './footer.module.scss'

import { MAX_FILE_SIZE } from 'constant'

type Props = {
  handleSendMessage: (message: HTMLTextAreaElement | null) => void
  conversation: Conversation
}

const ConversationFooter: FC<Props> = ({ handleSendMessage, conversation }): JSX.Element => {
  const { t } = useTranslation('legacy')
  const isMobileApp = Capacitor.isNativePlatform()
  const textAreaRef = useRef<HTMLTextAreaElement>(null)
  const [showEmojiPicker, setShowEmojiPicker] = useState<boolean>(false)
  const [showButton, setShowButton] = useState(false)

  useEffect(() => {
    if (textAreaRef.current) {
      textAreaRef.current.addEventListener('input', () => {
        const value = textAreaRef.current?.value

        if (value && value.trim().length > 0) {
          setShowButton(true)
        } else {
          setShowButton(false)
        }
      })
    }
  }, [setShowButton])

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.repeat) return
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault()
      sendMessage()
    } else {
      conversation.typing()
    }
  }

  const sendMessage = () => {
    if (textAreaRef.current) {
      handleSendMessage(textAreaRef.current)
      setShowButton(false)
    }
  }

  const handleEmojiSelect = (emoji: { native: string }) => {
    if (!textAreaRef.current) return
    textAreaRef.current.focus()
    const selectionStart = textAreaRef.current.selectionStart
    const selectionEnd = textAreaRef.current.selectionEnd

    textAreaRef.current.value = textAreaRef.current.value.slice(0, selectionStart) + emoji.native + textAreaRef.current.value.slice(selectionEnd)
    textAreaRef.current.dispatchEvent(new Event('input'))
    setShowEmojiPicker(false)
  }

  return (
    <footer className={styles.footer}>
      <textarea tabIndex={0} ref={textAreaRef} onKeyDown={handleKeyDown} placeholder={t('chat.CONVO.SEND_MESSAGE')} />
      <div className={styles.cta}>
        {!isMobileApp && <i role="button" onClick={() => setShowEmojiPicker(true)} className="fal fa-grin-alt"></i>}
        {showButton ? <i role="button" onClick={sendMessage} className="fal fa-paper-plane"></i> : null}
        <ConversationAttachment conversation={conversation} />
      </div>
      {showEmojiPicker ? <EmojiPicker onEmojiSelect={handleEmojiSelect} /> : null}
      <div onClick={() => setShowEmojiPicker(false)} className={`${showEmojiPicker && styles.overlay}`}></div>
    </footer>
  )
}

type ConversationAttachmentProps = {
  conversation: Conversation
}
const ConversationAttachment = ({ conversation }: ConversationAttachmentProps) => {
  const { t } = useTranslation('legacy')
  const attachmentIconId = useId()
  const handleFileChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.currentTarget.files?.[0]

    if (!file) return
    if (file.size > MAX_FILE_SIZE) return alert(t('forms.fileUpload.TOO_LARGE'))

    await conversation.sendMessage({
      filename: file?.name,
      media: file,
      contentType: file.type ?? 'Image/jpeg'
    })
  }
  return (
    <>
      <input
        onChange={handleFileChange}
        type="file"
        style={{ display: 'none' }}
        id={attachmentIconId}
        accept="image/jpeg,image/gif,image/png,application/pdf,image/x-eps"
      />
      <label style={{ margin: 0 }} htmlFor={attachmentIconId}>
        <i role="button" className="fa-light fa-paperclip"></i>
      </label>
    </>
  )
}
export { ConversationFooter }
